import { CdkDrag, CdkDragPreview, CdkDropList } from '@angular/cdk/drag-drop';
import { NgClass } from '@angular/common';
import { Component, EventEmitter, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject, input } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton, MatMiniFabButton } from '@angular/material/button';
import { MatCard, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatFooterCell,
    MatFooterCellDef,
    MatFooterRow,
    MatFooterRowDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { NomenclatureType } from '@app/core/models/nomenclature.model';
import { CommandEvents } from '@app/features/editeur/models/commands/command-events';
import { DropData, DropDataType } from '@app/features/editeur/models/drop-data';
import { Traitement } from '@app/shared/autorisations/traitements';
import { PageConverter } from '@app/shared/list/page.converter';
import { StructureService } from '@app/shared/structure/structure.service';
import { environment } from '@environments/environment';
import { PaginatedDataSource } from '@pcscol-portail/composants-pegase';
import { ObjetMaquetteSummary, ObjetsMaquetteService, TypeObjetMaquette } from '@pcscol/odf-api-v1-angular';
import { Subject, concat } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { EnableIfAutoriseDirective } from '../../../../../shared/autorisations/directives/enable-if-autorise.directive';
import { ShowIfAutoriseDirective } from '../../../../../shared/autorisations/directives/show-if-autorise.directive';
import { SelectTypeObjetMaquetteComponent } from '../../../../../shared/base-form/select-type-objet-maquette/select-type-objet-maquette.component';
import { PaginatorComponent } from '../../../../../shared/list/paginator/paginator.component';
import { SelectNomenclatureComponent } from '../../../../../shared/select-nomenclature/select-nomenclature/select-nomenclature.component';
import { TreeNode } from '../../treeview/tree-node';

interface SearchParams {
    texte?: string | null;
    typeObjetMaquette?: TypeObjetMaquette | null;
    typeObjetFormation?: string | null;
    racine?: boolean | null;
    mutualise?: boolean | null;
}

@Component({
    selector: 'app-bibliotheque',
    templateUrl: './bibliotheque.component.html',
    styleUrls: ['./bibliotheque.component.scss'],
    providers: [
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: {
                appearance: 'outline',
                subscriptSizing: 'dynamic'
            }
        }
    ],
    imports: [
        MatCard,
        MatCardHeader,
        MatCardTitle,
        MatCardContent,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatIconButton,
        MatSuffix,
        MatIcon,
        SelectTypeObjetMaquetteComponent,
        SelectNomenclatureComponent,
        MatCheckbox,
        MatTooltip,
        MatMiniFabButton,
        CdkDropList,
        EnableIfAutoriseDirective,
        MatTable,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatCellDef,
        MatCell,
        CdkDrag,
        CdkDragPreview,
        ShowIfAutoriseDirective,
        RouterLink,
        MatFooterCellDef,
        MatFooterCell,
        PaginatorComponent,
        MatRowDef,
        MatRow,
        NgClass,
        MatHeaderRowDef,
        MatHeaderRow,
        MatFooterRowDef,
        MatFooterRow
    ]
})
export class BibliothequeComponent implements OnInit, OnDestroy, OnChanges {
    private fb = inject(FormBuilder);
    private service = inject(ObjetsMaquetteService);
    private structureService = inject(StructureService);
    private events = inject(CommandEvents);
    private route = inject(ActivatedRoute);

    readonly NomenclatureType = NomenclatureType;
    readonly ODF_AJOUTER_ENFANT = Traitement.ODF_AJOUTER_ENFANT;
    readonly ODF_RECHERCHER_OBJET_MAQUETTE = Traitement.ODF_RECHERCHER_OBJET_MAQUETTE;
    readonly ODF_LIRE_STRUCTURE_MAQUETTE = Traitement.ODF_LIRE_STRUCTURE_MAQUETTE;
    readonly espaceId = input<string | null>();
    readonly parentPourAjoutEnfant = input<TreeNode>();
    readonly DNDDisabled = input<boolean>(false);
    @Output() ajouterEnfantSous: EventEmitter<{ parent: TreeNode; enfant: ObjetMaquetteSummary }> = new EventEmitter();
    readonly PIA: DropData = { type: DropDataType.PIA };
    readonly form = this.fb.group({
        texte: this.fb.control<string | null>(null),
        typeObjetMaquette: this.fb.control<TypeObjetMaquette | null>(null),
        typeObjetFormation: this.fb.control(null),
        racine: this.fb.control(false),
        mutualise: this.fb.control(false)
    });
    dataSource: PaginatedDataSource<ObjetMaquetteSummary, SearchParams>;
    readonly columns = ['codeDND', 'libelle', 'actions'];
    readonly columnsModeAjoutEnfant = ['code', 'libelle', 'ajouter'];
    private pageConverter = new PageConverter();
    private destroyed$ = new Subject<boolean>();

    ngOnInit(): void {
        this.form.controls?.typeObjetFormation.disable();
        this.loadSearchParamsFromStorage();
        this.setDataSource();
        // ajouter sûrement aussi le objetMaquetteModifie si le code a été modifié, gmblblblbl
        concat(this.events.objetMaquetteSupprime$, this.events.objetMaquetteCree$)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => this.dataSource.queryBy(this.form.value));

        this.form.controls.typeObjetMaquette.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((typeOM: TypeObjetMaquette | null) => {
            if (typeOM === TypeObjetMaquette.ObjetFormation) {
                this.form.controls?.typeObjetFormation.enable();
            } else {
                this.form.controls?.typeObjetFormation.reset();
                this.form.controls?.typeObjetFormation.disable();
            }
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.espaceId) {
            this.setDataSource();
        }
    }

    ajouterEnfant(enfant: ObjetMaquetteSummary) {
        this.ajouterEnfantSous.emit({ parent: this.parentPourAjoutEnfant()!, enfant });
    }

    ngOnDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    private loadSearchParamsFromStorage() {
        const savedSearchParams = localStorage.getItem('searchParams');
        if (savedSearchParams) {
            const parsedSearchParams = JSON.parse(savedSearchParams);
            // Patch form values
            this.form.patchValue({ ...parsedSearchParams });

            if (parsedSearchParams.typeObjetMaquette === TypeObjetMaquette.ObjetFormation) {
                this.form.get('typeObjetFormation')?.setValue(parsedSearchParams.typeObjetFormation);
                this.form.controls?.typeObjetFormation.enable();
            }
        }
    }

    private saveSearchParamsToStorage() {
        const formValue = this.form.value;
        localStorage.setItem('searchParams', JSON.stringify(formValue));
    }

    private setDataSource() {
        this.dataSource = new PaginatedDataSource<ObjetMaquetteSummary, SearchParams>(
            (req, searchParams) =>
                this.service
                    .rechercherObjetMaquette(
                        this.structureService.current$.value,
                        this.pageConverter.convertPageRequestToApi(req),
                        searchParams?.texte ?? undefined,
                        this.espaceId() ?? undefined,
                        searchParams?.typeObjetMaquette ? [searchParams?.typeObjetMaquette] : undefined,
                        searchParams?.racine ?? undefined,
                        searchParams?.typeObjetFormation ?? undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        searchParams?.mutualise ?? undefined
                    )
                    .pipe(map(response => this.pageConverter.convertPageToModel(response))),
            { order: 'asc', property: 'code' },
            undefined,
            environment.pagination.pageSize
        );
    }

    rechercher() {
        this.dataSource.queryBy(this.form.value ?? {});
        this.saveSearchParamsToStorage();
    }

    get objetMaquetteOuvertId(): string | null {
        return this.route.snapshot.paramMap.get('id');
    }

    estEnfantDuparentPourAjout(element): boolean {
        return this.parentPourAjoutEnfant()?.enfants.some(e => e.objetMaquette.id === element.id) ?? false;
        return true;
    }

    dropData(objetMaquette: ObjetMaquetteSummary): DropData {
        return { type: DropDataType.OBJET_MAQUETTE, objetMaquetteId: objetMaquette.id };
    }
}
